import React, { useContext } from 'react';
import { RuleOutputDisplayData } from '@decisions/server/DecisionsFramework/Design/Flow/Service';
import { EditableTextItem } from '../components';
import { IntersectionEditorInfo } from 'components/inputs/InlineEditor/InlineEditor';
import { Cell } from '.';
import { ValidationIcon } from 'domains/designers/common';
import { IAllGroupModel } from 'domains/designers/RuleDesigner/models/AllGroupModel';
import { getOutputIssues } from 'domains/designers/TruthTable/TruthTableUtils';
import { RuleExecutionResult } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Debugging/Events';
import { getResultBackgroundColor } from 'domains/designers/TruthTable/TruthTableUtils';
import { OutputColumn } from 'domains/designers/TruthTable/columns';
import { RuleDesignerContext } from 'domains/designers/RuleDesigner/RuleDesignerContext';

export interface SetValueEventObj {
  intersection: IntersectionEditorInfo;
  value: string;
  output: RuleOutputDisplayData;
  model: IAllGroupModel;
}

interface IOutputCellProps extends React.HTMLProps<HTMLTableDataCellElement> {
  model: IAllGroupModel;
  output: RuleOutputDisplayData;
  executionResult: RuleExecutionResult | null;
  onEditClick: (event: { target: HTMLElement; output: RuleOutputDisplayData; model: IAllGroupModel }) => void;
  setValue: (event: SetValueEventObj) => void;
  showFullEditor?: (event: { editorInfo: IntersectionEditorInfo }) => void;
}

/**
 * Contains output which will be returned for truly outcome.
 * You rather need to use [[OutputCells]] as it contains logic to display outputs correctly corresponding to RuleType.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function OutputCell({
  className,
  output,
  model,
  executionResult,
  setValue,
  onEditClick,
  showFullEditor,
  ...otherProps
}: IOutputCellProps) {
  const { isDebug } = useContext(RuleDesignerContext);
  const outputIssues = getOutputIssues(model.GetValidations(), output.DataDescriptionName);
  const displayText = output.MappingText ?? '[no data]';

  return (
    <OutputColumn
      {...otherProps}
      style={{ backgroundColor: getResultBackgroundColor(executionResult) }}
      data-column-name={output.DisplayName}
    >
      <Cell>
        <ValidationIcon validationIssues={outputIssues} />
        <EditableTextItem
          text={displayText}
          isInDebug={isDebug}
          onEditClick={e => {
            onEditClick?.({
              target: e.target as HTMLElement,
              output,
              model,
            });
          }}
          intersection={{
            mappingType: output.MappingType,
            dataType: output.DataType,
            displayValue: output.Value,
            canNavigate: false,
            commands: {
              setIntersectionStringValue: async (intersection, value) => {
                await setValue({
                  intersection,
                  value,
                  output,
                  model,
                });
              },
              showFullEditor: intersection => showFullEditor?.({ editorInfo: intersection }),
            },
          }}
        />
      </Cell>
    </OutputColumn>
  );
}
